import { useState } from "react"
import { Navigate } from "react-router-dom"
import { skipToken } from "@reduxjs/toolkit/query/react"
import { IconLoader2, IconTrash, IconWand } from "@tabler/icons-react"
import { toast } from "sonner"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Skeleton } from "@/components/ui/skeleton"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import type { BookingDetails } from "@/app/booking-calendar/bookingCalendarApi"
import { AffectedBookingsList } from "@/app/booking-calendar/components/AffectedBookingsList"
import { SITE_TIME_ZONE } from "@/app/booking-calendar/lib/siteTime"
import {
  type ScheduleOverrideDetail,
  useGetResolveOptionsQuery,
  useGetScheduleOverrideQuery,
  useRevokeScheduleOverrideMutation,
} from "../schedule-overrides/scheduleOverridesApi"
import { affectsBookings, SITE_CLOSURE } from "../schedule-overrides/lib/overridePreview"
import {
  describeTarget,
  EVENT_TYPE_LABELS,
  formatDateRange,
  formatWindow,
  kindLabel,
  PROJECTION_STATUS_LABELS,
} from "../schedule-overrides/lib/overrideForm"
import { releaseNotes } from "../schedule-overrides/lib/releaseNotice"
import { RESOLUTION_OPTIONS, type ResolutionOptionId } from "../availability/lib/overrideEditModel"
import { ResolutionChoiceDialog } from "../availability/components/ResolutionChoiceDialog"
import { AutomaticReassignmentDialog } from "./AutomaticReassignmentDialog"
import { withServerState } from "./automaticReassignment"
import { manualReschedulePath } from "../availability/resolve/paths"

/**
 * What the detail offers for an override already saved (AB#810): the resolution choice less "Proceed
 * without rescheduling", which only means something before saving. Automatic reassignment says nothing
 * about saving here, since the override already exists.
 */
const DETAIL_RESOLUTION_OPTIONS = RESOLUTION_OPTIONS
  .filter(option => option.id !== "proceed")
  .map(option =>
    option.id === "automatic"
      ? {
          ...option,
          description:
            "Move each appointment to the earliest weekday past the 28-day booking window that fits. You see the "
            + "plan and confirm it before anything moves.",
        }
      : option,
  )

// ── Withdrawing ──────────────────────────────────────────────────────

/** Tells the person how a withdrawal went, from the revoke's answer. */
export function announceWithdrawal(result: ScheduleOverrideDetail) {
  const summary = result.override

  if (summary.status === 4) {
    toast.warning("Partly withdrawn.", {
      description: "Some days could not be put back. Withdraw it again to retry them.",
    })
  } else if (summary.conflictedDays > 0) {
    toast.warning("Withdrawn.", {
      description: `${summary.conflictedDays} day(s) had been changed by something else since, so they were left as they are.`,
    })
  } else {
    toast.success("Override withdrawn.")
  }
}

/** Tells the person a withdrawal was refused. */
export function announceWithdrawalFailure(err: unknown) {
  const status = (err as { status?: number }).status
  toast.error(
    status === 409
      ? "Another override change is in progress. Try again in a moment."
      : "Could not withdraw the override. Try again in a moment.",
  )
}

// ── The detail ───────────────────────────────────────────────────────

/** Another override on the same slot, which the detail can switch to. */
export interface OverrideChoice {
  id: string
  label: string
}

/**
 * Every day an override wrote, on which schedule, and whether it is still in Cal — and, for a manager,
 * the booked appointments it affects, each with its quick edits (AB#769). Opened from the availability
 * week's markers (AB#803).
 *
 * The note shows when the server sent one: the detail endpoint sends it to appointments:manage and to
 * in-scope view-only callers alike (decision ams-0d0 pending), so this only checks for it. With `withdrawable` a
 * manager also gets Withdraw, and `onWithdrawn` hears when it lands. `choices` lists the other
 * overrides on the slot that opened it, to switch between.
 */
export function OverrideDetailDialog({
  id,
  onClose,
  canManage,
  withdrawable = false,
  onWithdrawn,
  choices,
  onChoose,
}: {
  id: string | null
  onClose: () => void
  canManage: boolean
  withdrawable?: boolean
  onWithdrawn?: (id: string) => void
  choices?: readonly OverrideChoice[]
  onChoose?: (id: string) => void
}) {
  // currentData, not data: another override's detail is never shown while this one loads. Not
  // replaced by a skeleton while refetching either — each action on a booking refetches it, and the
  // list has to stay put, with its results, underneath the action's dialog.
  const { currentData: data } = useGetScheduleOverrideQuery(id ?? "", { skip: id === null })
  const [revoke, { isLoading: revoking }] = useRevokeScheduleOverrideMutation()
  const [confirming, setConfirming] = useState(false)

  const canWithdraw = withdrawable && canManage && !!data && data.override.status !== 3

  async function withdraw() {
    if (!id) return
    try {
      announceWithdrawal(await revoke(id).unwrap())
      setConfirming(false)
      onWithdrawn?.(id)
    } catch (err) {
      announceWithdrawalFailure(err)
      setConfirming(false)
    }
  }

  return (
    <Dialog open={id !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{data ? `${kindLabel(data.override.kind)} — ${describeTarget(data.override)}` : "Override"}</DialogTitle>
          <DialogDescription>
            {data &&
              `${formatWindow(data.override.startDate, data.override.startTime, data.override.endDate, data.override.endTime)} · ` +
                `created by ${data.override.createdBy}` +
                (data.override.revokedBy ? ` · withdrawn by ${data.override.revokedBy}` : "")}
          </DialogDescription>
        </DialogHeader>

        {choices && choices.length > 1 && (
          <nav aria-label="Overrides on this slot" className="flex flex-wrap items-center gap-2 text-sm">
            <span className="text-muted-foreground">{choices.length} overrides change this slot:</span>
            {choices.map(choice => (
              <Button
                key={choice.id}
                size="sm"
                variant={choice.id === id ? "secondary" : "outline"}
                aria-current={choice.id === id ? "true" : undefined}
                onClick={() => onChoose?.(choice.id)}
              >
                {choice.label}
              </Button>
            ))}
          </nav>
        )}

        {data?.override.note && (
          <p className="text-sm" data-testid="override-note">
            <span className="text-muted-foreground">Note: </span>
            {data.override.note}
          </p>
        )}

        {!data ? (
          <Skeleton className="h-32 w-full" />
        ) : data.projections.length === 0 ? (
          <p className="text-sm text-muted-foreground">It changed nothing.</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Day</TableHead>
                <TableHead>Schedule</TableHead>
                <TableHead>Hours</TableHead>
                <TableHead>State</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.projections.map((p) => (
                <TableRow key={`${p.calScheduleId}:${p.date}`}>
                  <TableCell className="whitespace-nowrap">{formatDateRange(p.date, p.date)}</TableCell>
                  <TableCell>
                    <div>{p.teamName ?? p.teamSlug}</div>
                    <div className="text-xs text-muted-foreground">
                      {p.isSiteSchedule
                        ? `${p.eventTypeSlug ? EVENT_TYPE_LABELS[p.eventTypeSlug] : "Site"} hours (whole site)`
                        : `${data.override.hostName ?? data.override.hostEmail ?? "Host"}'s hours`}
                    </div>
                  </TableCell>
                  <TableCell className="whitespace-nowrap">
                    {p.windows === "00:00-00:00"
                      ? p.span
                        ? `Closed ${p.span}`
                        : "Closed"
                      : p.windows.replaceAll(",", ", ")}
                  </TableCell>
                  <TableCell>
                    <div>{PROJECTION_STATUS_LABELS[p.status]}</div>
                    {p.detail && <div className="text-xs text-muted-foreground">{p.detail}</div>}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}

        {data && <AffectedBookingsSection detail={data} canManage={canManage} />}

        {canWithdraw && (
          <div className="flex justify-end pt-2">
            <Button variant="outline" className="text-destructive hover:text-destructive" onClick={() => setConfirming(true)}>
              <IconTrash className="size-4" />
              Withdraw
            </Button>
          </div>
        )}
      </DialogContent>

      <AlertDialog open={confirming} onOpenChange={(open) => !open && !revoking && setConfirming(false)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Withdraw this override?</AlertDialogTitle>
            <AlertDialogDescription>
              {data && (
                <>
                  {kindLabel(data.override.kind)} — {describeTarget(data.override)},{" "}
                  {formatWindow(data.override.startDate, data.override.startTime, data.override.endDate, data.override.endTime)}. The
                  days it changed go back to what they held before. Days changed by something else since are left alone.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={revoking}>Keep it</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault()
                void withdraw()
              }}
              disabled={revoking}
            >
              {revoking && <IconLoader2 className="size-4 animate-spin" />}
              Withdraw
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  )
}

/**
 * The booked appointments an override affects, read afresh with the detail — so the list empties as
 * each is dealt with — under the days it wrote. Shown only when the server sent them: to a manager, for
 * an out of office or a site closure still in force.
 *
 * A manager also gets "Resolve affected bookings" while any are listed
 * (AB#810): the resolution choice for a saved override — automatic reassignment, enabled or not as the
 * server's options say — then the plan, and the batch's progress; the detail and the week are read
 * again when it finishes. Or manual reschedule (AB#811), when the server's options open it: the manual
 * reschedule workspace for this override.
 */
export function AffectedBookingsSection({ detail, canManage }: { detail: ScheduleOverrideDetail; canManage: boolean }) {
  const { override, bookings } = detail
  const [choosing, setChoosing] = useState(false)
  // The bookings as they stood when automatic reassignment was chosen, to name the customers while
  // the list empties under it.
  const [resolving, setResolving] = useState<readonly BookingDetails[] | null>(null)
  // Manual reschedule chosen: the workspace is navigated to. A <Navigate> rather than useNavigate, so
  // the detail still renders outside a router.
  const [manual, setManual] = useState(false)
  const optionsQuery = useGetResolveOptionsQuery(choosing ? override.id : skipToken, { refetchOnMountOrArgChange: true })

  if (!bookings || !affectsBookings(override.kind)) return null

  const offerResolve = canManage && bookings.length > 0 && override.status !== 3
  const options = withServerState(DETAIL_RESOLUTION_OPTIONS, optionsQuery.currentData, {
    loading: optionsQuery.isFetching,
    error: optionsQuery.error,
  })

  function choose(id: ResolutionOptionId) {
    if (id === "manual") {
      if (!options.some(o => o.id === "manual" && o.enabled)) return
      setChoosing(false)
      setManual(true)
      return
    }
    if (id !== "automatic") return
    setChoosing(false)
    setResolving(bookings ?? [])
  }

  return (
    <section className="flex flex-col gap-2 pt-2">
      {offerResolve ? (
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 className="font-medium">Booked appointments in this window</h3>
          <Button size="sm" variant="outline" onClick={() => setChoosing(true)} data-testid="resolve-affected-bookings">
            <IconWand className="size-4" />
            Resolve affected bookings
          </Button>
        </div>
      ) : (
        <h3 className="font-medium">Booked appointments in this window</h3>
      )}
      <p className="text-sm text-muted-foreground">
        Not cancelled by the override. Move, release or cancel each one; the list keeps them until they are all dealt with.
      </p>
      <AffectedBookingsList
        bookings={bookings}
        timeZone={SITE_TIME_ZONE}
        canManage={canManage}
        offerReassign={override.kind !== SITE_CLOSURE}
        releaseNotes={(booking) => releaseNotes(booking, override.id)}
        emptyMessage="No booked appointments are left in this window."
      />

      {manual && <Navigate to={manualReschedulePath(override.id)} />}

      {canManage && (
        <>
          <ResolutionChoiceDialog
            open={choosing}
            count={bookings.length}
            options={options}
            busy={false}
            onChoose={choose}
            onCancel={() => setChoosing(false)}
            prompt="Choose how to resolve them."
            cancelLabel="Cancel"
          />
          <AutomaticReassignmentDialog
            overrideId={resolving ? override.id : null}
            bookings={resolving}
            onClose={() => setResolving(null)}
          />
        </>
      )}
    </section>
  )
}
